export const ROLE_DRIVER = 'driver';

/**
 * Check if a user has the driver role — either as default_role (driver-only)
 * or in usertoroles (combo user with portal + driver).
 */
export function hasDriverRole(
  defaultRole: string,
  usertoroles?: Array<{ roletype?: { name?: string } | null }>,
): boolean {
  if (defaultRole === ROLE_DRIVER) return true;
  return usertoroles?.some(r => r.roletype?.name === ROLE_DRIVER) ?? false;
}

/** Role granted to a driver who is still completing onboarding (registered via the app). */
export const ROLE_PENDING_DRIVER = 'pending-driver';

/**
 * Return the driver-family role that grants the user mobile-app access:
 * the full `driver` role (takes precedence) or `pending-driver` while they
 * are still onboarding, or null if they have neither. Checks both
 * default_role and usertoroles (a combo admin+driver carries it in usertoroles).
 */
export function getDriverAccessRole(
  defaultRole: string,
  usertoroles?: Array<{ roletype?: { name?: string } | null }>,
): typeof ROLE_DRIVER | typeof ROLE_PENDING_DRIVER | null {
  const names = new Set<string>();
  if (defaultRole) names.add(defaultRole);
  usertoroles?.forEach(r => {
    if (r.roletype?.name) names.add(r.roletype.name);
  });
  if (names.has(ROLE_DRIVER)) return ROLE_DRIVER;
  if (names.has(ROLE_PENDING_DRIVER)) return ROLE_PENDING_DRIVER;
  return null;
}

export const ROLE_DEALER = 'dealer';
export const ROLE_DEALER_ADMIN = 'dealer-admin';
export const ROLE_DEALER_SUPER_ADMIN = 'dealer-super-admin';
export const ROLE_PROGRAM_ADMIN = 'program-admin';

/** All dealer-tier roles (for GQL _in filters) */
export const DEALER_ROLES = [ROLE_DEALER, ROLE_DEALER_ADMIN, ROLE_DEALER_SUPER_ADMIN] as const;

/** Internal HopDrive staff. */
export const ROLE_ADMIN = 'admin';
export const ROLE_DISPATCHER = 'dispatcher';

/** Roles internal to HopDrive — staff who live under /users rather than /portal-users. */
export const INTERNAL_ROLES: readonly string[] = [ROLE_ADMIN, ROLE_DISPATCHER];

/**
 * True for the three dealer-tier portal roles. Deliberately an exact match
 * against DEALER_ROLES rather than a substring test for 'dealer' — this decides
 * who an admin may provision as a driver, so a future role that merely contains
 * the word must not opt itself in.
 */
export function isDealerTierRole(role: string): boolean {
  return (DEALER_ROLES as readonly string[]).includes(role);
}

/** Role options for dropdowns and toggle groups */
export const ROLE_OPTIONS = [
  { value: ROLE_DRIVER, label: 'Driver' },
  { value: ROLE_DEALER, label: 'Dealer' },
  { value: ROLE_DEALER_ADMIN, label: 'Dealer Admin' },
  { value: ROLE_DEALER_SUPER_ADMIN, label: 'Dealer Super Admin' },
] as const;

/** Role descriptions for tooltips */
export const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'Access to admin portal',
  [ROLE_DRIVER]: 'Access to the mobile app',
  [ROLE_DEALER]: 'Access to a single customer',
  [ROLE_DEALER_ADMIN]: 'Access to multiple customers',
  [ROLE_DEALER_SUPER_ADMIN]: 'Access to all customers and user management by default',
};

/** Mapping of role keys to display labels */
export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  [ROLE_DRIVER]: 'Driver',
  [ROLE_DEALER]: 'Dealer',
  [ROLE_DEALER_ADMIN]: 'Dealer Admin',
  [ROLE_DEALER_SUPER_ADMIN]: 'Dealer Super Admin',
  [ROLE_PROGRAM_ADMIN]: 'Program Admin',
};

/** Get the human-readable label for a role key. */
export function getRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/** Per-role chip color config: bg + text color */
export interface RoleChipStyle { bg: string; color: string }

export const ROLE_CHIP_STYLES: Record<string, RoleChipStyle> = {
  admin:                     { bg: '#fff5f3', color: '#ff4830' },
  [ROLE_DEALER_SUPER_ADMIN]: { bg: '#fff5f3', color: '#ff4830' },
  [ROLE_DEALER_ADMIN]:       { bg: '#eef4fb', color: '#4068a0' },
  [ROLE_DEALER]:             { bg: '#eef4fb', color: '#4068a0' },
  [ROLE_DRIVER]:             { bg: '#fff9f3', color: '#a6681f' },
  [ROLE_PROGRAM_ADMIN]:      { bg: '#f3f0fb', color: '#6840a0' },
};

const DEFAULT_CHIP_STYLE: RoleChipStyle = { bg: '#f4f4f5', color: '#60666d' };

export function getRoleChipStyle(role: string): RoleChipStyle {
  return ROLE_CHIP_STYLES[role] ?? DEFAULT_CHIP_STYLE;
}

export interface RoleUser {
  id: number;
  default_role: string;
  active: boolean;
}

/**
 * Determine if a user's role should change based on their new customer count.
 * - dealer → dealer-admin when linked to ≥2 customers
 * - dealer-admin → dealer when linked to ≤1 customer
 * Returns the target role string, or null if no change is needed.
 */
export function calculateTargetRole(
  currentRole: string,
  newCustomerCount: number,
): string | null {
  if (newCustomerCount >= 2 && currentRole === ROLE_DEALER) {
    return ROLE_DEALER_ADMIN;
  }
  if (newCustomerCount <= 1 && currentRole === ROLE_DEALER_ADMIN) {
    return ROLE_DEALER;
  }
  return null;
}

/**
 * Check whether a user has access to a specific customer.
 */
export function userHasCustomerAccess(
  userCustomerIds: number[],
  customerId: number,
): boolean {
  return userCustomerIds.includes(customerId);
}

/**
 * Check if a role is dealer-super-admin.
 */
export function isSuperAdmin(role: string): boolean {
  return role === ROLE_DEALER_SUPER_ADMIN;
}

/**
 * Count active super admins in a list of users.
 */
export function countActiveSuperAdmins(users: RoleUser[]): number {
  return users.filter(u => u.active && isSuperAdmin(u.default_role)).length;
}

/**
 * Returns true only if demoting the given user still leaves ≥1 other active super admin.
 */
export function canDemoteSuperAdmin(users: RoleUser[], userId: number): boolean {
  const otherActiveSuperAdmins = users.filter(
    u => u.id !== userId && u.active && isSuperAdmin(u.default_role),
  );
  return otherActiveSuperAdmins.length >= 1;
}

/**
 * Returns true unless the user is the sole active super admin.
 */
export function canDeactivateUser(users: RoleUser[], userId: number): boolean {
  const user = users.find(u => u.id === userId);
  if (!user || !isSuperAdmin(user.default_role)) return true;
  return canDemoteSuperAdmin(users, userId);
}

/**
 * Returns true when zero active super admins exist in the list.
 */
export function shouldDefaultToSuperAdmin(users: RoleUser[]): boolean {
  return countActiveSuperAdmins(users) === 0;
}

/**
 * Derive the default_role for a new user based on their access configuration.
 *
 * - Driver-only (no portal access) → 'driver'
 * - Super admin → 'dealer-super-admin'
 * - 2+ rooftops → 'dealer-admin'
 * - 1 rooftop → 'dealer'
 */
export function deriveRole(
  isSuperAdmin: boolean,
  portalAccess: boolean,
  mobileAccess: boolean,
  customerCount: number,
): string {
  if (!portalAccess && mobileAccess) return ROLE_DRIVER;
  if (isSuperAdmin) return ROLE_DEALER_SUPER_ADMIN;
  if (customerCount >= 2) return ROLE_DEALER_ADMIN;
  return ROLE_DEALER;
}
